import { useMemo } from 'react';

import { PageModule, ZoneConfig } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';

export type TreatmentTypeSetting = {
  treatmentType: string;
  treatmentTypeKey?: string | null;
  label?: string | null;
  minRequiredContentItems: number;
  maxRequiredContentItems: number;
  optional?: boolean | null;
  contentTypes: string[];
  message?: string | null;
  module?: PageModule | null;
  ignoreBaseQuery?: boolean | null;
  skipContentParameters?: boolean | null;
  requiredProperties?: string[] | null;
  zones?: ZoneConfig[] | null;
};

const DEFAULTS: Omit<TreatmentTypeSetting, 'treatmentType'> = {
  treatmentTypeKey: null,
  label: null,
  minRequiredContentItems: 0,
  maxRequiredContentItems: 0,
  optional: null,
  contentTypes: [],
  message: null,
  module: null,
  ignoreBaseQuery: null,
  skipContentParameters: null,
  requiredProperties: null,
  zones: null
};

export const useTreatmentTypeSettings = (publicationKey: string): TreatmentTypeSetting[] => {
  const { data: pubData } = usePublicationSettings({ publicationKey });

  return useMemo((): TreatmentTypeSetting[] => {
    const raw = pubData?.publicationSetting.treatmentTypes as
      | Record<string, Record<string, unknown>>
      | null
      | undefined;
    if (!raw) return [];
    return Object.entries(raw).map(
      ([key, entry]): TreatmentTypeSetting => ({
        ...DEFAULTS,
        contentTypes: [],
        treatmentType: key,
        ...(typeof entry.treatmentTypeKey === 'string' && { treatmentTypeKey: entry.treatmentTypeKey }),
        ...(typeof entry.label === 'string' && { label: entry.label }),
        ...(typeof entry.minRequiredContentItems === 'number' && {
          minRequiredContentItems: entry.minRequiredContentItems
        }),
        ...(typeof entry.maxRequiredContentItems === 'number' && {
          maxRequiredContentItems: entry.maxRequiredContentItems
        }),
        ...(typeof entry.optional === 'boolean' && { optional: entry.optional }),
        ...(Array.isArray(entry.contentTypes) && { contentTypes: entry.contentTypes as string[] }),
        ...(typeof entry.message === 'string' && { message: entry.message }),
        ...((entry.module ?? entry.defaultModule) != null && {
          module: (entry.module ?? entry.defaultModule) as PageModule
        }),
        ...(typeof entry.ignoreBaseQuery === 'boolean' && { ignoreBaseQuery: entry.ignoreBaseQuery }),
        ...(typeof entry.skipContentParameters === 'boolean' && {
          skipContentParameters: entry.skipContentParameters
        }),
        ...(Array.isArray(entry.requiredProperties) && {
          requiredProperties: entry.requiredProperties as string[]
        }),
        ...(Array.isArray(entry.zones) && {
          zones: entry.zones as ZoneConfig[]
        })
      })
    );
  }, [pubData]);
};
